// #tag: SAVED OPPORTUNITIES LOCAL STORAGE HOOK
// #purpose: Manages browser localStorage bookmarks for career opportunities without requiring immediate login.
import { useCallback, useEffect, useState } from 'react';
import type { SavedOpportunity } from '../types';

const STORAGE_KEY = 'wfx-results-saved-opportunities';

function loadSaved(): SavedOpportunity[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw) as SavedOpportunity[];
  } catch {
    /* ignore */
  }
  return [];
}

export function useSavedOpportunities() {
  const [saved, setSaved] = useState<SavedOpportunity[]>(loadSaved);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(saved));
    } catch {
      /* ignore */
    }
  }, [saved]);

  const isSaved = useCallback(
    (id: string) => saved.some((item) => item.id === id),
    [saved],
  );

  const toggleSave = useCallback((item: SavedOpportunity) => {
    setSaved((prev) => {
      if (prev.some((entry) => entry.id === item.id)) {
        return prev.filter((entry) => entry.id !== item.id);
      }
      return [{ ...item, savedAt: item.savedAt || new Date().toISOString() }, ...prev];
    });
  }, []);

  return { savedOpportunities: saved, isSaved, toggleSave };
}
